@let t = text();

<div class="hd-notes-panel">
  @if (error()) {
    <div class="hd-notes-panel__error">{{ error() }}</div>
  }

  @if (!savedChartId()) {
    <div class="hd-notes-panel__empty">{{ t.saveFirst }}</div>
  } @else if (loading()) {
    <div class="hd-notes-panel__empty">{{ t.loading }}</div>
  } @else {
    <!-- Eigen notities van de chart -->
    <section class="hd-notes-panel__section">
      <h4 class="hd-notes-panel__title">
        <span>{{ t.chartNotesTitle }}</span>
        @if (!editingChartNote()) {
          <span class="hd-notes-panel__title-actions">
            @if (undockable()) {
              <button type="button" class="hd-notes-panel__title-action"
                      [attr.aria-label]="isEditing() ? t.undockWhileEditing : t.undock"
                      [title]="isEditing() ? t.undockWhileEditing : t.undock"
                      [disabled]="isEditing()"
                      (click)="requestUndock()"
                      data-test-id="notes-panel-undock">
                <i class="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
              </button>
            }
            <!-- Dit moment als Markdown exporteren (uitleg in de exportdialoog). -->
            @if (isMoment()) {
              <button type="button" class="hd-notes-panel__title-action"
                      [attr.aria-label]="t.exportMoment" [title]="t.exportMoment"
                      (click)="openExport()"
                      data-test-id="notes-panel-export">
                <i class="fas fa-file-export" aria-hidden="true"></i>
              </button>
            }
            <button type="button" class="hd-notes-panel__title-action"
                    [attr.aria-label]="t.edit" [title]="t.edit"
                    (click)="startChartNoteEdit()">
              <i class="fas fa-pen" aria-hidden="true"></i>
            </button>
          </span>
        } @else {
          <!-- Save + annuleren; save spiegelt de knop onderin de composer, na saven wordt dit weer het potloodje. -->
          <span class="hd-notes-panel__title-actions">
            <button type="button" class="hd-notes-panel__title-action"
                    [attr.aria-label]="t.save" [title]="t.save"
                    [disabled]="savingChartNote()"
                    (click)="submitChartNoteFromHeader()"
                    data-test-id="notes-panel-header-save">
              <i class="fas fa-floppy-disk" aria-hidden="true"></i>
            </button>
            <button type="button" class="hd-notes-panel__title-action"
                    [attr.aria-label]="t.cancel" [title]="t.cancel"
                    [disabled]="savingChartNote()"
                    (click)="cancelChartNoteEdit()"
                    data-test-id="notes-panel-header-cancel">
              <i class="fas fa-xmark" aria-hidden="true"></i>
            </button>
          </span>
        }
      </h4>

      @if (editingChartNote()) {
        <support-comment-composer
          #chartNoteComposer
          [sending]="savingChartNote()"
          [initialText]="chartNotes() ?? ''"
          [placeholder]="t.notePlaceholder"
          [sendLabel]="t.save"
          sendIcon="floppy-disk"
          [allowEmpty]="true"
          screenshot="in-place"
          (send)="saveChartNote($event)"
        ></support-comment-composer>
        <!-- Bestaande bijlagen onder de composer, waar ook nieuwe drafts verschijnen. -->
        @if (chartAttachments().length > 0) {
          <div class="hd-notes-panel__existing-thumbs">
            @for (attachment of chartAttachments(); track attachment.id) {
              <div class="hd-notes-panel__thumb">
                @if (attachmentUrl(attachment.id); as url) {
                  <img [src]="url" [alt]="attachment.fileName ?? ''" (click)="openViewer(attachment.id)" />
                } @else {
                  <span class="hd-notes-panel__thumb-name">{{ attachment.fileName }}</span>
                }
                <button type="button" class="hd-notes-panel__thumb-remove"
                        [attr.aria-label]="t.removeAttachment" [title]="t.removeAttachment"
                        (click)="removeChartAttachment(attachment.id!)">
                  <i class="fas fa-xmark" aria-hidden="true"></i>
                </button>
              </div>
            }
          </div>
        }
        <button type="button" class="hd-notes-panel__cancel" (click)="cancelChartNoteEdit()">
          {{ t.cancel }}
        </button>
      } @else {
        @if (chartNotes()) {
          <div class="hd-notes-panel__note support-markup-body" [innerHTML]="chartNotesHtml()"></div>
        } @else {
          <div class="hd-notes-panel__empty">{{ t.empty }}</div>
        }
        @if (chartAttachments().length > 0) {
          <div class="hd-notes-panel__thumbs">
            @for (attachment of chartAttachments(); track attachment.id) {
              @if (attachmentUrl(attachment.id); as url) {
                <img class="hd-notes-panel__thumb-img" [src]="url" [alt]="attachment.fileName ?? ''"
                     (click)="openViewer(attachment.id)" />
              } @else {
                <span class="hd-notes-panel__thumb-name">{{ attachment.fileName }}</span>
              }
            }
          </div>
        }
      }
    </section>

    <!-- Per link de notities van die link (klik de link om ze te zien/bewerken);
         standaard dichtgeklapt, met filter en paginering (feedback 2026-09-17). -->
    <section class="hd-notes-panel__section">
      <h4 class="hd-notes-panel__title">
        <span>{{ t.linksTitle }}</span>
        <!-- Indicator (finetune 2026-09-21): ook dichtgeklapt zichtbaar dat er linknotities zijn. -->
        @if (!linksExpanded() && linksWithNotesCount() > 0) {
          <span class="hd-notes-panel__links-note-indicator" [title]="t.hasNote"
                data-test-id="notes-panel-links-indicator">
            <i class="fas fa-note-sticky" aria-hidden="true"></i>{{ linksWithNotesCount() }}
          </span>
        }
        <button type="button" class="hd-notes-panel__title-action"
                [attr.aria-expanded]="linksExpanded()"
                [attr.aria-label]="linksExpanded() ? t.hideLinks : t.showLinks"
                [title]="linksExpanded() ? t.hideLinks : t.showLinks"
                (click)="toggleLinksExpanded()"
                data-test-id="notes-panel-links-toggle">
          <i class="fas fa-chevron-right hd-notes-panel__chevron"
             [class.hd-notes-panel__chevron--open]="linksExpanded()"
             aria-hidden="true"></i>
        </button>
      </h4>

      @if (linksExpanded()) {
      <label class="hd-notes-panel__links-filter">
        <input type="checkbox" [checked]="onlyWithNotes()"
               (change)="setOnlyWithNotes($any($event.target).checked)"
               data-test-id="notes-panel-links-filter" />
        {{ t.onlyWithNotes }}
      </label>

      @if (filteredLinkRows().length === 0) {
        <div class="hd-notes-panel__empty">{{ t.noLinks }}</div>
      }
      @for (row of pagedLinkRows(); track row.relation.id) {
        <div class="hd-notes-panel__link">
          <button type="button" class="hd-notes-panel__link-row"
                  [attr.aria-expanded]="expandedRelationId() === row.relation.id"
                  (click)="toggleRelation(row.relation.id)">
            <i [class]="'fas fa-' + row.icon + ' hd-notes-panel__link-icon'" aria-hidden="true"></i>
            <span class="hd-notes-panel__link-type">{{ row.typeLabel }}</span>
            <span class="hd-notes-panel__link-name">{{ row.name }}</span>
            @if (row.hasNote) {
              <i class="fas fa-note-sticky hd-notes-panel__indicator" [title]="t.hasNote" aria-hidden="true"></i>
            }
            @if (row.attachmentCount > 0) {
              <span class="hd-notes-panel__indicator" [title]="t.hasAttachments">
                <i class="fas fa-paperclip" aria-hidden="true"></i>{{ row.attachmentCount }}
              </span>
            }
            <i class="fas fa-chevron-right hd-notes-panel__chevron"
               [class.hd-notes-panel__chevron--open]="expandedRelationId() === row.relation.id"
               aria-hidden="true"></i>
          </button>

          @if (expandedRelationId() === row.relation.id) {
            <div class="hd-notes-panel__link-detail">
              <!-- Zelfde patroon als de chartnotitie-header: potlood in weergave, save tijdens bewerken. -->
              <div class="hd-notes-panel__link-detail-header">
                @if (editingRelationId() === row.relation.id) {
                  <button type="button" class="hd-notes-panel__title-action"
                          [attr.aria-label]="t.save" [title]="t.save"
                          [disabled]="savingRelationNote()"
                          (click)="submitRelationNoteFromHeader()"
                          data-test-id="notes-panel-link-header-save">
                    <i class="fas fa-floppy-disk" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="hd-notes-panel__title-action"
                          [attr.aria-label]="t.cancel" [title]="t.cancel"
                          [disabled]="savingRelationNote()"
                          (click)="cancelRelationNoteEdit()"
                          data-test-id="notes-panel-link-header-cancel">
                    <i class="fas fa-xmark" aria-hidden="true"></i>
                  </button>
                } @else {
                  <button type="button" class="hd-notes-panel__title-action"
                          [attr.aria-label]="t.edit" [title]="t.edit"
                          (click)="startRelationNoteEdit(row.relation.id)"
                          data-test-id="notes-panel-link-header-edit">
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                }
              </div>
              @if (editingRelationId() === row.relation.id) {
                <support-comment-composer
                  #relationNoteComposer
                  [sending]="savingRelationNote()"
                  [initialText]="row.relation.note ?? ''"
                  [placeholder]="t.notePlaceholder"
                  [sendLabel]="t.save"
                  sendIcon="floppy-disk"
                  [allowEmpty]="true"
                  screenshot="in-place"
                  (send)="saveRelationNote(row.relation, $event)"
                ></support-comment-composer>
                <!-- Bestaande bijlagen onder de composer, waar ook nieuwe drafts verschijnen. -->
                @if ((row.relation.noteAttachments ?? []).length > 0) {
                  <div class="hd-notes-panel__existing-thumbs">
                    @for (attachment of row.relation.noteAttachments ?? []; track attachment.id) {
                      <div class="hd-notes-panel__thumb">
                        @if (attachmentUrl(attachment.id); as url) {
                          <img [src]="url" [alt]="attachment.fileName ?? ''" (click)="openViewer(attachment.id)" />
                        } @else {
                          <span class="hd-notes-panel__thumb-name">{{ attachment.fileName }}</span>
                        }
                        <button type="button" class="hd-notes-panel__thumb-remove"
                                [attr.aria-label]="t.removeAttachment" [title]="t.removeAttachment"
                                (click)="removeRelationAttachment(attachment.id!)">
                          <i class="fas fa-xmark" aria-hidden="true"></i>
                        </button>
                      </div>
                    }
                  </div>
                }
                <button type="button" class="hd-notes-panel__cancel" (click)="cancelRelationNoteEdit()">
                  {{ t.cancel }}
                </button>
              } @else {
                @if (row.hasNote) {
                  <div class="hd-notes-panel__note support-markup-body" [innerHTML]="row.noteHtml"></div>
                } @else {
                  <div class="hd-notes-panel__empty">{{ t.empty }}</div>
                }
                @if ((row.relation.noteAttachments ?? []).length > 0) {
                  <div class="hd-notes-panel__thumbs">
                    @for (attachment of row.relation.noteAttachments ?? []; track attachment.id) {
                      @if (attachmentUrl(attachment.id); as url) {
                        <img class="hd-notes-panel__thumb-img" [src]="url" [alt]="attachment.fileName ?? ''"
                             (click)="openViewer(attachment.id)" />
                      } @else {
                        <span class="hd-notes-panel__thumb-name">{{ attachment.fileName }}</span>
                      }
                    }
                  </div>
                }
                <div class="hd-notes-panel__link-actions">
                  <button type="button" class="hd-notes-panel__link-action" (click)="startRelationNoteEdit(row.relation.id)">
                    <i class="fas fa-pen" aria-hidden="true"></i> {{ t.edit }}
                  </button>
                  @if (row.relation.otherChartId) {
                    <button type="button" class="hd-notes-panel__link-action" (click)="openLinkedChart(row.relation)">
                      {{ t.openChart }}
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      }

      @if (filteredLinkRows().length > linksPageSize) {
        <!-- appendTo body: in het scrollende dockpaneel zou de dropdown anders
             een extra scrollbar veroorzaken i.p.v. eroverheen te vallen. -->
        <e-paginator
          [currentPage]="linksPage()"
          [pageSize]="linksPageSize"
          [totalCount]="filteredLinkRows().length"
          dropdownAppendTo="body"
          (currentPageChange)="linksPage.set($event)"
        ></e-paginator>
      }
      }
    </section>
  }

  <!-- Grote weergave van één afbeelding -->
  @if (viewerUrl(); as url) {
    <div class="hd-notes-panel__viewer" (click)="closeViewer()">
      <img [src]="url" alt="" />
      <button type="button" class="hd-notes-panel__viewer-close"
              [attr.aria-label]="t.closeViewer" [title]="t.closeViewer"
              (click)="closeViewer()">
        <i class="fas fa-xmark" aria-hidden="true"></i>
      </button>
    </div>
  }
</div>

<hd-moments-export-dialog [(target)]="exportTarget"></hd-moments-export-dialog>
